/**
 * @module @next-qr/lib-ui/presence
 *
 * connectPresence — opens a persistent WebSocket to /ws/presence and
 * reconnects with exponential backoff on disconnect.
 *
 * Designed to be called once at the app root level. Handles guest/unregistered
 * users gracefully: if getToken() returns null, no connection is attempted.
 *
 * @example
 * // In the root component:
 * useEffect(() => connectPresence(getAccessToken), []);
 *
 * @example
 * // With a custom base URL (e.g. in a mini-app):
 * useEffect(() => connectPresence(getAccessToken, { getBaseURL: () => 'https://api.example.com' }), []);
 */

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS  = 30_000;
const WS_PATH = '/ws/presence';

/**
 * Decode a JWT and return its expiry timestamp (seconds), or null if
 * the token is malformed. Does NOT verify the signature.
 */
function jwtExpiry(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof json.exp === 'number' ? json.exp : null;
  } catch {
    return null;
  }
}

/** Returns true if the token's exp is in the past (with a 5 s leeway). */
function isTokenExpired(token: string): boolean {
  const exp = jwtExpiry(token);
  if (exp === null) return false; // can't tell — try connecting anyway
  return exp < Math.floor(Date.now() / 1000) + 5;
}

/**
 * Derive the WebSocket base URL from an HTTP base URL string.
 * e.g. "https://api.example.com" → "wss://api.example.com"
 *      "http://localhost:3000"   → "ws://localhost:3000"
 */
function httpToWs(baseUrl: string): string {
  return `${baseUrl || ''}`.replace(/^https:\/\//, 'wss://').replace(/^http:\/\//, 'ws://');
}

/**
 * Get the WebSocket root URL (no path) from the current page location.
 */
function defaultWsRoot(): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}`;
}

export interface ConnectPresenceOptions {
  /** Returns the HTTP base URL (e.g. from openapi-client config). Defaults to window.location. */
  getBaseURL?: () => string;
  /**
   * localStorage key to watch for token refreshes. Defaults to '@next-qr:mini-app-store:access_token'.
   * When this key changes, presence reconnects immediately with the new token.
   */
  tokenStorageKey?: string;
}

/**
 * Open a WebSocket connection to /ws/presence.
 * Reconnects automatically with exponential backoff.
 *
 * @param getToken   - Returns the current JWT access token, or null if not authenticated.
 * @param options    - Optional configuration.
 * @returns Cleanup function — call to stop reconnecting and close the socket.
 */
export function connectPresence(
  getToken: () => string | null,
  options: ConnectPresenceOptions = {},
): () => void {
  let ws: WebSocket | null = null;
  let stopped = false;
  let retryDelay = RECONNECT_BASE_MS;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  function connect() {
    if (stopped) return;

    const token = getToken();
    if (!token) {
      // No token — user is not authenticated. Wait for token-refreshed event.
      return;
    }

    if (isTokenExpired(token)) {
      // Token is expired — don't open a connection that will immediately be rejected.
      // Wait for a fresh token via the token-refreshed or storage event.
      return;
    }

    const wsRoot = options.getBaseURL
      ? httpToWs(options.getBaseURL())
      : defaultWsRoot();

    if(!wsRoot) {
      console.error('Failed to determine WebSocket root URL for presence connection');
      return;
    }

    const url = `${wsRoot}${WS_PATH}?token=${encodeURIComponent(token)}`;

    try {
      ws = new WebSocket(url);
    } catch {
      scheduleReconnect();
      return;
    }

    ws.onopen = () => {
      retryDelay = RECONNECT_BASE_MS; // reset backoff on successful connection
    };

    ws.onclose = (ev) => {
      ws = null;
      // 4401 = server rejected the token (invalid/expired).
      // Don't retry on a timer — wait for the storage event instead.
      // The axios refresh interceptor will write a new token to localStorage,
      // which triggers onStorageChange → connect() with the fresh token.
      if (ev.code === 4401) return;
      if (!stopped) scheduleReconnect();
    };

    ws.onerror = () => {
      // onclose fires after onerror; no need to reconnect here
    };
  }

  function scheduleReconnect() {
    if (stopped) return;
    retryTimer = setTimeout(() => {
      retryDelay = Math.min(retryDelay * 2, RECONNECT_MAX_MS);
      connect();
    }, retryDelay);
  }

  // Reconnect immediately when a fresh token is available.
  // Two event sources cover both same-tab and cross-tab token refreshes:
  //   1. 'next-qr:token-refreshed' — custom event dispatched by the axios interceptor
  //      in the same tab after a successful refresh (window.storage does NOT fire for
  //      changes made by the same tab, so this covers that gap).
  //   2. 'storage' — fires in OTHER tabs when localStorage is written.
  function onTokenRefreshed() {
    if (ws?.readyState === WebSocket.OPEN) return; // already connected
    if (retryTimer !== null) { clearTimeout(retryTimer); retryTimer = null; }
    retryDelay = RECONNECT_BASE_MS;
    connect();
  }

  function onStorageChange(ev: StorageEvent) {
    const watchKey = options.tokenStorageKey ?? '@next-qr:mini-app-store:access_token';
    if (ev.key !== watchKey) return;
    if (!ev.newValue) return; // token removed — stay disconnected
    onTokenRefreshed();
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('next-qr:token-refreshed', onTokenRefreshed);
    window.addEventListener('storage', onStorageChange);
  }

  connect();

  return function cleanup() {
    stopped = true;
    if (typeof window !== 'undefined') {
      window.removeEventListener('next-qr:token-refreshed', onTokenRefreshed);
      window.removeEventListener('storage', onStorageChange);
    }
    if (retryTimer !== null) clearTimeout(retryTimer);
    if (ws) {
      ws.onclose = null; // prevent reconnect on intentional close
      ws.close();
      ws = null;
    }
  };
}
